import React, { useState, useEffect } from 'react';
import './BrowserCompatibility.css';

const BrowserCompatibility = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [compatibilityIssues, setCompatibilityIssues] = useState([]);

  useEffect(() => {
    checkCompatibility();
  }, []);

  const checkCompatibility = () => {
    const issues = [];
    
    // Check WebGL support
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) {
      issues.push({
        type: 'critical',
        title: 'WebGL Not Supported',
        description: 'Your browser doesn\'t support WebGL, which is required for 3D galleries. Please update your browser or enable WebGL in settings.',
        solution: 'Update to a modern browser like Chrome, Firefox, Safari, or Edge.'
      });
    }

    // Check if mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      issues.push({
        type: 'warning',
        title: 'Mobile Device Detected',
        description: 'Gallery performance may be limited on mobile devices. For the best experience, use a desktop or laptop computer.',
        solution: 'Some features may not work optimally on mobile browsers.'
      });
    }

    // Check for very old browsers
    const isOldBrowser = !window.fetch || !window.Promise || !document.querySelector;
    if (isOldBrowser) {
      issues.push({
        type: 'critical',
        title: 'Outdated Browser',
        description: 'Your browser is too old to support this application. Please update to a modern browser.',
        solution: 'Update to Chrome 60+, Firefox 55+, Safari 12+, or Edge 79+.'
      });
    }

    // Check for WebAssembly support (used by some 3D features)
    if (!window.WebAssembly) {
      issues.push({
        type: 'warning',
        title: 'Limited Performance',
        description: 'WebAssembly is not supported, which may affect 3D performance and some advanced features.',
        solution: 'Consider updating your browser for better performance.'
      });
    }

    // Check memory (rough estimate for complex 3D scenes)
    if (navigator.deviceMemory && navigator.deviceMemory < 2) {
      issues.push({
        type: 'warning',
        title: 'Low Device Memory',
        description: 'Your device has limited memory. Large galleries may load slowly or cause performance issues.',
        solution: 'Close other browser tabs and applications for better performance.'
      });
    }

    // Check if localStorage is available
    try {
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
    } catch (e) {
      issues.push({
        type: 'warning',
        title: 'Storage Limited',
        description: 'Local storage is not available. Some preferences may not be saved.',
        solution: 'Check your browser\'s privacy settings or try an incognito window.'
      });
    }

    setCompatibilityIssues(issues);
    
    // Show warning if there are critical issues or multiple warnings
    const criticalIssues = issues.filter(issue => issue.type === 'critical');
    const warningIssues = issues.filter(issue => issue.type === 'warning');
    
    if (criticalIssues.length > 0 || warningIssues.length > 2) {
      setShowWarning(true);
    }
  };

  const dismissWarning = () => {
    setShowWarning(false);
    // Remember dismissal for this session
    sessionStorage.setItem('compatibility-warning-dismissed', 'true');
  };

  const criticalIssues = compatibilityIssues.filter(issue => issue.type === 'critical');
  const warningIssues = compatibilityIssues.filter(issue => issue.type === 'warning');

  if (!showWarning || sessionStorage.getItem('compatibility-warning-dismissed')) {
    return null;
  }

  return (
    <div className="compatibility-overlay">
      <div className="compatibility-modal">
        <div className="compatibility-header">
          <h2>
            {criticalIssues.length > 0 ? '⚠️ Browser Compatibility Issues' : '⚠️ Performance Warning'}
          </h2>
          {criticalIssues.length === 0 && (
            <button 
              className="compatibility-close"
              onClick={dismissWarning}
              aria-label="Dismiss warning"
            >
              ×
            </button>
          )}
        </div>
        
        <div className="compatibility-content">
          {criticalIssues.length > 0 && (
            <div className="compatibility-section critical">
              <h3>Critical Issues (May Prevent App from Working)</h3>
              {criticalIssues.map((issue, index) => (
                <div key={index} className="compatibility-issue">
                  <h4>{issue.title}</h4>
                  <p>{issue.description}</p>
                  <div className="compatibility-solution">
                    <strong>Solution:</strong> {issue.solution}
                  </div>
                </div>
              ))}
            </div>
          )}

          {warningIssues.length > 0 && (
            <div className="compatibility-section warning">
              <h3>Performance Warnings</h3>
              {warningIssues.map((issue, index) => (
                <div key={index} className="compatibility-issue">
                  <h4>{issue.title}</h4>
                  <p>{issue.description}</p>
                  <div className="compatibility-solution">
                    <strong>Recommendation:</strong> {issue.solution}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="compatibility-recommendations">
            <h3>Recommended Browsers</h3>
            <ul>
              <li><strong>Chrome 88+</strong> - Best performance and feature support</li>
              <li><strong>Firefox 85+</strong> - Good performance with privacy features</li>
              <li><strong>Safari 14+</strong> - Good for macOS and iOS users</li>
              <li><strong>Edge 88+</strong> - Modern Chromium-based browser</li>
            </ul>
          </div>
        </div>

        <div className="compatibility-footer">
          {criticalIssues.length > 0 ? (
            <div className="compatibility-actions">
              <button 
                className="btn-secondary"
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </button>
              <button 
                className="btn-primary"
                onClick={dismissWarning}
              >
                Continue Anyway
              </button>
            </div>
          ) : (
            <div className="compatibility-actions">
              <button 
                className="btn-primary"
                onClick={dismissWarning}
              >
                I Understand, Continue
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrowserCompatibility;